import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControlLabel,
  Switch,
  Chip,
  Divider,
  useTheme,
  alpha,
  Card,
  CardContent,
  CardHeader,
  Paper,
  InputAdornment,
  IconButton,
  Stack,
  Tooltip,
  Fade
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import SaveIcon from '@mui/icons-material/Save';
import CategoryIcon from '@mui/icons-material/Category';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ViewListIcon from '@mui/icons-material/ViewList';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { httpRequest } from '../../../../services/httpRequest';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux-store/appStore';

interface CategoryData {
  CategoryID: number;
  CategoryName: string;
  Active: boolean;
  RowStatus: number;
  CreateDate: string;
  CreatedBy: number;
  UpdateDate: string | null;
  UpdatedBy: number | null;
  DeleteDate: string | null;
  DeletedBy: number | null;
  TownshipID: number;
}

interface ApiResponse {
  status: string;
  message: string;
  data: {
    totalRecords: number;
    currentPage: number;
    totalPages: number;
    data: CategoryData[];
  };
}

interface FormData {
  categoryName: string;
  active: boolean;
}

interface TouchedFields {
  categoryName: boolean;
}

const BidCategories: React.FC = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState<FormData>({
    categoryName: '',
    active: true
  });
  const [touched, setTouched] = useState<TouchedFields>({
    categoryName: false
  });
  const [categories, setCategories] = useState<CategoryData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<{
    text: string;
    type: 'success' | 'error' | 'info' | undefined;
  }>({ text: '', type: undefined });
  const [selectedCategory, setSelectedCategory] = useState<CategoryData | null>(
    null
  );
  const [editMode, setEditMode] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const userRole = userDetails.UserRole || '';

  // Debounce the search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // Debounce delay (500ms)

    return () => {
      clearTimeout(timer); // Clear the previous timer on each change
    };
  }, [searchTerm]);

  // Fetch categories with debounced search term
  const fetchCategories = async () => {
    setTableLoading(true);
    try {
      const response: ApiResponse = await httpRequest(
        'GET',
        `/bid_portal/category/get_all?Page=${
          page + 1
        }&Size=${rowsPerPage}&Active=false${
          debouncedSearchTerm ? `&Category=${debouncedSearchTerm}` : ''
        }`
      );

      if (response && response.data) {
        setCategories(response.data.data);
        setTotalCount(response.data.totalRecords);
      }
    } catch (error) {
      setMessage({
        text: 'Failed to fetch categories',
        type: 'error'
      });
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [page, rowsPerPage, debouncedSearchTerm]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleBlur = (fieldName: keyof TouchedFields) => {
    setTouched((prev) => ({
      ...prev,
      [fieldName]: true
    }));
  };

  const handleClear = () => {
    setFormData({ categoryName: '', active: true });
    setTouched({ categoryName: false });
    setEditMode(false);
    setSelectedCategory(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    setTouched({ categoryName: true });

    if (!formData.categoryName) {
      return;
    }

    setLoading(true);
    try {
      if (editMode && selectedCategory) {
        const res = await httpRequest(
          'PATCH',
          `/bid_portal/category/update/${selectedCategory.CategoryID}`,
          {
            CategoryName: formData.categoryName,
            Active: formData.active
          }
        );

        if (res.status === 'success') {
          setMessage({
            text: res.message || `Category successfully updated`,
            type: 'success'
          });
          handleClear();
          fetchCategories();
        } else {
          setMessage({
            text:
              res.message || 'An error occurred while updating the category.',
            type: 'error'
          });
        }
      } else {
        const res = await httpRequest('POST', '/bid_portal/category/insert', {
          CategoryName: formData.categoryName
        });

        if (res.status === 'success') {
          setMessage({
            text: res.message || `Category successfully created`,
            type: 'success'
          });
          handleClear();
          fetchCategories();
        } else {
          setMessage({
            text:
              res.message || 'An error occurred while creating the category.',
            type: 'error'
          });
        }
      }
    } catch (error) {
      setMessage({
        text: error.message || 'An error occurred while saving the category.',
        type: 'success'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category: CategoryData) => {
    setFormData({
      categoryName: category.CategoryName,
      active: category.Active
    });
    setTouched({ categoryName: false });
    setSelectedCategory(category);
    setEditMode(true);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const showError = touched.categoryName && !formData.categoryName;

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        bgcolor: alpha(theme.palette.background.default, 0.4),
        borderRadius: 2,
        minHeight: '100vh'
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 4 }}>
        <CategoryIcon
          sx={{
            fontSize: 32,
            color: theme.palette.primary.main
          }}
        />
        <Typography
          variant="h4"
          sx={{
            color: theme.palette.primary.main,
            fontWeight: 'bold'
          }}
        >
          Bid Categories
        </Typography>
      </Stack>

      {message.text && (
        <Fade in={!!message.text}>
          <Alert
            severity={message.type}
            sx={{
              mb: 3,
              boxShadow: 2,
              borderRadius: 2
            }}
            onClose={() => setMessage({ text: '', type: undefined })}
          >
            {message.text}
          </Alert>
        </Fade>
      )}
      {userRole === 'Admin' && (
        <Card
          elevation={2}
          sx={{
            mb: 4,
            borderRadius: 2,
            overflow: 'visible'
          }}
        >
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {editMode ? (
                  <EditIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                ) : (
                  <AddCircleOutlineIcon
                    sx={{ mr: 1, color: theme.palette.primary.main }}
                  />
                )}
                <Typography variant="h6">
                  {editMode ? 'Edit Category' : 'Create New Category'}
                </Typography>
                {editMode && selectedCategory && (
                  <Chip
                    label={`ID: ${selectedCategory.CategoryID}`}
                    size="small"
                    sx={{
                      ml: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      color: theme.palette.info.main
                    }}
                  />
                )}
              </Box>
            }
            sx={{
              bgcolor: alpha(theme.palette.primary.light, 0.1),
              pb: 1
            }}
          />
          <CardContent sx={{ p: 3 }}>
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    fullWidth
                    label="Category Name"
                    name="categoryName"
                    value={formData.categoryName}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur('categoryName')}
                    error={showError}
                    helperText={showError ? 'Category name is required' : ''}
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: 1 },
                      startAdornment: (
                        <InputAdornment position="start">
                          <CategoryIcon
                            sx={{ color: theme.palette.primary.main }}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>

                {editMode && (
                  <Grid item xs={12} md={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        backgroundColor: alpha(
                          formData.active
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                          0.05
                        ),
                        border: `1px solid ${alpha(
                          formData.active
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                          0.2
                        )}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {formData.active ? (
                          <CheckCircleOutlineIcon
                            sx={{ color: theme.palette.success.main, mr: 1 }}
                          />
                        ) : (
                          <HighlightOffIcon
                            sx={{ color: theme.palette.error.main, mr: 1 }}
                          />
                        )}
                        <Typography
                          variant="body1"
                          sx={{
                            color: formData.active
                              ? theme.palette.success.main
                              : theme.palette.error.main,
                            fontWeight: 500
                          }}
                        >
                          {formData.active ? 'Active' : 'Inactive'}
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.active}
                            onChange={handleSwitchChange}
                            name="active"
                            color={formData.active ? 'success' : 'error'}
                          />
                        }
                        label=""
                        sx={{ m: 0 }}
                      />
                    </Paper>
                  </Grid>
                )}

                <Grid item xs={12} md={editMode ? 2 : 6}>
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                  >
                    <Button
                      onClick={handleClear}
                      variant="outlined"
                      disabled={loading}
                      startIcon={<ClearIcon />}
                      sx={{
                        borderRadius: 1,
                        px: 2
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      variant="contained"
                      color="primary"
                      disabled={loading}
                      startIcon={
                        loading ? (
                          <CircularProgress size={20} />
                        ) : editMode ? (
                          <SaveIcon />
                        ) : (
                          <AddCircleOutlineIcon />
                        )
                      }
                      sx={{
                        borderRadius: 1,
                        px: 3
                      }}
                    >
                      {loading ? 'Saving...' : editMode ? 'Update' : 'Create'}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      )}

      <Card elevation={2} sx={{ borderRadius: 2 }}>
        <CardHeader
          title={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ViewListIcon
                  sx={{ mr: 1, color: theme.palette.primary.main }}
                />
                <Typography variant="h6">All Categories</Typography>
                <Chip
                  label={`${totalCount} total`}
                  size="small"
                  sx={{
                    ml: 2,
                    bgcolor: alpha(theme.palette.primary.light, 0.1),
                    color: theme.palette.primary.main
                  }}
                />
              </Box>

              <TextField
                placeholder="Search categories..."
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="primary" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchTerm('')}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.background.default, 0.7)
                  }
                }}
                sx={{ width: { xs: '100%', sm: 250 } }}
              />
            </Box>
          }
          sx={{
            bgcolor: alpha(theme.palette.background.default, 0.5),
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
        />

        <TableContainer sx={{ maxHeight: 650 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    bgcolor: theme.palette.background.paper,
                    py: 2
                  }}
                >
                  Category
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    bgcolor: theme.palette.background.paper
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarTodayIcon sx={{ mr: 1, fontSize: 18 }} />
                    Created At
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    bgcolor: theme.palette.background.paper
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarTodayIcon sx={{ mr: 1, fontSize: 18 }} />
                    Updated At
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    bgcolor: theme.palette.background.paper
                  }}
                >
                  Status
                </TableCell>

                {userRole === 'Admin' && (
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      bgcolor: theme.palette.background.paper
                    }}
                  >
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={30} />
                    <Typography
                      variant="body2"
                      sx={{ mt: 2, color: theme.palette.text.secondary }}
                    >
                      Loading categories...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {searchTerm
                        ? `No categories matching "${searchTerm}" found`
                        : 'No categories found'}
                    </Typography>
                    {searchTerm && (
                      <Button
                        variant="text"
                        color="primary"
                        startIcon={<ClearIcon />}
                        onClick={() => setSearchTerm('')}
                        sx={{ mt: 1 }}
                      >
                        Clear Search
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                categories.map((category) => (
                  <TableRow
                    key={category.CategoryID}
                    sx={{
                      '&:nth-of-type(odd)': {
                        bgcolor: alpha(theme.palette.background.default, 0.3)
                      },
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.light, 0.05)
                      }
                    }}
                  >
                    <TableCell sx={{ py: 2, fontWeight: 'medium' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CategoryIcon
                          sx={{
                            mr: 1,
                            color: theme.palette.primary.main,
                            fontSize: 18
                          }}
                        />
                        {category.CategoryName}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={category.CreateDate} arrow>
                        <Typography variant="body2">
                          {formatDate(category.CreateDate)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={category.UpdateDate || ''} arrow>
                        <Typography variant="body2">
                          {formatDate(category.UpdateDate)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={category.Active ? 'Active' : 'Inactive'}
                        icon={
                          category.Active ? (
                            <CheckCircleOutlineIcon />
                          ) : (
                            <HighlightOffIcon />
                          )
                        }
                        sx={{
                          backgroundColor: category.Active
                            ? alpha(theme.palette.success.main, 0.1)
                            : alpha(theme.palette.error.main, 0.1),
                          color: category.Active
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                          fontWeight: 500,
                          borderRadius: 1,
                          '& .MuiChip-icon': {
                            color: 'inherit'
                          }
                        }}
                      />
                    </TableCell>

                    {userRole === 'Admin' && (
                      <TableCell align="center">
                        <Tooltip title="Edit Category" arrow>
                          <Button
                            onClick={() => handleEdit(category)}
                            size="small"
                            variant="text"
                            color="primary"
                            startIcon={<EditIcon fontSize="small" />}
                            sx={{
                              borderRadius: 1,
                              '&:hover': {
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.04
                                )
                              }
                            }}
                          >
                            Edit
                          </Button>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider />
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
              {
                my: 1
              }
          }}
        />
      </Card>

      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha(theme.palette.background.default, 0.7),
            backdropFilter: 'blur(4px)',
            zIndex: 9999
          }}
        >
          <Paper
            elevation={4}
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 2
            }}
          >
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="subtitle1">
              {editMode ? 'Updating category...' : 'Creating category...'}
            </Typography>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default BidCategories;
